import React, { useEffect, useState } from "react";
import type { AaMatchIssue, ReleaseCandidatesResponse } from "../shared/contracts.ts";

export const AA_MISSING_LABELS: Record<AaMatchIssue, string> = {
  "no-source-match": "No matching AA release found",
  "creator-mismatch": "AA lab identity needs review",
  "serving-variant": "Serving variant; canonical model needed",
  "checkpoint-changed": "Updated model identity needs review",
  "invalid-effort-metadata": "Reasoning settings need review",
  "ambiguous-source-match": "Multiple AA runs; match needs review",
  "unsupported-effort": "AA reasoning configuration needs review",
  "score-not-published": "AA release found; score not published",
};

export function parseReleaseCandidates(value: unknown): ReleaseCandidatesResponse {
  const data = value as ReleaseCandidatesResponse;
  const instant = (value: unknown) => typeof value === "string" && Number.isFinite(Date.parse(value));
  const link = (value: unknown) => {
    try { return typeof value === "string" && new URL(value).protocol === "https:"; } catch { return false; }
  };
  if (!data || data.schemaVersion !== "1.0" || data.verified !== false || !instant(data.generatedAt)
    || typeof data.aaVersion !== "string" || !(data.aaVerifiedAt === null || instant(data.aaVerifiedAt))
    || !(data.aaError === null || typeof data.aaError === "string") || !Array.isArray(data.items)
    || data.items.some((item) => !item || !Number.isSafeInteger(item.id) || typeof item.title !== "string"
      || !["reddit", "openrouter"].includes(item.source) || !["text", "multimodal"].includes(item.category)
      || !instant(item.publishedAt) || !link(item.url) || typeof item.aaEstimated !== "boolean"
      || !(item.aaScore === null || (typeof item.aaScore === "number" && Number.isFinite(item.aaScore)))
      || !(item.aaUrl === null || link(item.aaUrl)))) throw new Error("Invalid release candidates response");
  if (data.items.some((item) => item.aaMissingReason != null && !Object.hasOwn(AA_MISSING_LABELS, item.aaMissingReason))) throw new Error("Invalid AA match reason");
  return data;
}

export function ReleaseCandidates(): React.ReactElement {
  const [data, setData] = useState<ReleaseCandidatesResponse | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const response = await fetch("/api/release-candidates", { cache: "no-store", signal: controller.signal });
        if (!response.ok) throw new Error("Release feed unavailable");
        const next = parseReleaseCandidates(await response.json());
        if (!controller.signal.aborted) { setData(next); setError(false); }
      } catch { if (!controller.signal.aborted) setError(true); }
      finally { if (!controller.signal.aborted) timer = setTimeout(() => void load(), 300_000); }
    };
    void load();
    return () => { controller.abort(); clearTimeout(timer); };
  }, []);
  const items = [...(data?.items ?? [])].sort((left, right) => Date.parse(right.publishedAt) - Date.parse(left.publishedAt) || right.id - left.id);
  return <section className="release-feed-card" aria-labelledby="release-feed-heading">
    <h2 className="eyebrow" id="release-feed-heading">Latest detected releases</h2>
    <p>Jev-classified text &amp; multimodal candidates · not independently verified.</p>
    {error && <p role="status">Release feed unavailable. {data ? "Showing the last loaded results." : "Retrying automatically."}</p>}
    {!data && !error && <p role="status">Loading release candidates…</p>}
    {data && <>
      <p className="release-feed-freshness">AA v{data.aaVersion} · {data.aaError ? "Refresh failed; retained scores shown" : data.aaVerifiedAt ? `Checked ${new Date(data.aaVerifiedAt).toLocaleString("en", { timeZone: "UTC" })} UTC` : "First refresh pending"}</p>
      {!items.length ? <p>No classified releases in the latest candidates.</p> : <ol className="release-feed-list">
        {items.map((item) => <li key={item.id}>
          <div><a href={item.url} target="_blank" rel="noreferrer">{item.title} ↗</a>
            <span>{item.category} · {item.source === "reddit" ? "Posted" : "Cataloged"} <time dateTime={item.publishedAt}>{new Date(item.publishedAt).toLocaleDateString("en", { month: "short", day: "numeric", timeZone: "UTC" })}</time></span></div>
          {item.aaScore !== null && item.aaUrl ? <a className="release-feed-score" href={item.aaUrl} target="_blank" rel="noreferrer">{item.aaScore.toFixed(1)}<small>AA{item.aaEstimated ? " estimate" : ""} ↗</small></a>
            : <span className="release-feed-unscored">{item.source === "reddit" ? "Model match pending" : item.aaMissingReason ? AA_MISSING_LABELS[item.aaMissingReason] : "AA score not matched"}</span>}
          <small className="release-feed-origin">{item.source}</small>
        </li>)}
      </ol>}
    </>}
  </section>;
}
